import { RefObject } from 'react'
import {
  BoldIcon,
  CodeIcon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
  StrikethroughIcon,
  ImagePlusIcon,
  CodeSquareIcon,
  TableIcon,
  MessageSquareQuoteIcon,
  Smile,
} from 'lucide-react'
import { cn, Separator, UploadFileProps } from '@wellesley/ui'
import { EmojiClickData } from '@wellesley/emoji-picker'
import { UploadItem } from '@wellesley/uploads'
import { EmojiDropdown } from 'components/EmojiDropdown'
import { ToolbarButton } from '../ToolbarButton'
import { MarkdownActions } from '../MarkdownEditorState/actions'
import OverflowRow from '../../OverflowRow'
import { MarkdownAddMediaButton } from './MarkdownAddMediaButton'

type ToolbarProps = {
  className?: string
  actions: MarkdownActions
  onAddMedia?: (files: UploadFileProps[]) => void
  attachments?: UploadItem[]
  editorRef?: RefObject<HTMLTextAreaElement>
}

const MarkdownToolbar = ({
  className,
  actions,
  onAddMedia,
  attachments,
  editorRef,
}: ToolbarProps) => {
  const handleEmojiClick = (emojiData: EmojiClickData) => {
    actions.insertText(emojiData.emoji)
  }
  return (
    <OverflowRow className={cn('gap-xxxs items-center', className)}>
      <ToolbarButton
        Icon={BoldIcon}
        ariaLabel="Bold"
        shortcut="Mod+B"
        onClick={actions.toggleBold}
      />
      <ToolbarButton
        Icon={ItalicIcon}
        ariaLabel="Italic"
        shortcut="Mod+I"
        onClick={actions.toggleItalic}
      />
      <ToolbarButton
        Icon={StrikethroughIcon}
        ariaLabel="Strike"
        shortcut="Mod+Shift+X"
        onClick={actions.toggleStrike}
      />
      <Separator orientation="vertical" size="xs" />
      <ToolbarButton
        Icon={ListIcon}
        ariaLabel="Bullet list"
        shortcut="Mod+Shift+L"
        onClick={actions.toggleUnorderedList}
      />
      <ToolbarButton
        Icon={ListOrderedIcon}
        ariaLabel="Ordered list"
        shortcut="Mod+Shift+O"
        onClick={actions.toggleOrderedList}
      />
      <ToolbarButton
        Icon={MessageSquareQuoteIcon}
        ariaLabel="Quote"
        shortcut="Mod+Shift+B"
        onClick={actions.toggleQuote}
      />
      <Separator orientation="vertical" size="xs" />
      <ToolbarButton
        Icon={CodeIcon}
        ariaLabel="Code"
        shortcut="Mod+Shift+C"
        onClick={actions.toggleInlineCode}
      />
      <ToolbarButton
        Icon={CodeSquareIcon}
        ariaLabel="Code block"
        shortcut="Mod+Alt+C"
        onClick={actions.insertCodeBlock}
      />
      <Separator orientation="vertical" size="xs" />
      <ToolbarButton
        Icon={LinkIcon}
        ariaLabel="Link"
        shortcut="Mod+K"
        onClick={actions.insertLink}
      />
      {onAddMedia ? (
        <MarkdownAddMediaButton
          onAddMedia={onAddMedia}
          actions={actions}
          attachments={attachments}
          editorRef={editorRef}
        />
      ) : (
        <ToolbarButton
          Icon={ImagePlusIcon}
          ariaLabel="Image"
          onClick={actions.insertImage}
        />
      )}
      <ToolbarButton
        Icon={TableIcon}
        ariaLabel="Table"
        shortcut="Mod+Alt+T"
        onClick={actions.insertTable}
      />
      <Separator orientation="vertical" size="xs" />
      <EmojiDropdown
        trigger={
          <ToolbarButton
            Icon={Smile}
            ariaLabel="Emoji"
            // shortcut="Mod+Alt+E" ?
          />
        }
        onEmojiClick={handleEmojiClick}
      />
    </OverflowRow>
  )
}

export { MarkdownToolbar }
